'use client';

import React, { useEffect, useRef } from 'react';

const steps = [
{
  num: '01',
  title: 'Free Home Survey',
  body: 'John visits your home, measures every window precisely, and discusses your preferred style, louvre size, and finish.'
},
{
  num: '02',
  title: 'Your Bespoke Quote',
  body: 'You receive a clear, itemised quote with no hidden costs. No pressure, no salespeople — just an honest price.'
},
{
  num: '03',
  title: 'Shutters Manufactured',
  body: 'Your uPVC shutters are crafted to your exact specifications. UK-manufactured — typical lead time just 3–4 weeks.'
},
{
  num: '04',
  title: 'Professional Fitting',
  body: 'John installs everything himself, ensuring a perfect finish. Most homes are fully fitted within a single day.'
}];


export default function AboutSection() {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -40px 0px' }
    );
    if (leftRef.current) observer.observe(leftRef.current);
    if (rightRef.current) observer.observe(rightRef.current);
    if (stepsRef.current) observer.observe(stepsRef.current);
    if (bannerRef.current) observer.observe(bannerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* ── ABOUT SECTION ── */}
      <section
        id="about"
        className="py-32 bg-[#FAF9F6]"
        aria-labelledby="about-heading">
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Image */}
            <div
              ref={leftRef}
              className="relative reveal-hidden-left">
              
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="/assets/images/about-shutters.png"
                  alt="Elegant white plantation shutters fitted in a bright Norfolk home, showing the quality craftsmanship of John Keith Shutters"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Decorative sand block */}
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-[#E8DED1] rounded-2xl -z-10" />
            </div>

            {/* Content */}
            <div ref={rightRef} className="reveal-hidden-right">
              <p className="text-[#7B8A94] text-sm font-medium tracking-[0.25em] uppercase mb-4">
                About
              </p>
              <h2
                id="about-heading"
                className="font-display text-[#0F1C2E] text-4xl lg:text-5xl font-medium leading-tight mb-6">
                
                A Reliable, Trustworthy Process
              </h2>
              <p className="text-[#0F1C2E]/70 text-lg leading-relaxed mb-12">
                John Keith Shutters is a sole-trader business built on word-of-mouth
                and repeat customers. When you deal with us, you deal directly with
                John — a craftsman who takes personal pride in every installation
                across North Norfolk.
              </p>

              <div className="space-y-8">
                {[
                {
                  num: '1',
                  title: 'Personally Installed',
                  body: 'Every shutter is measured and fitted by John himself. You will never meet a salesperson or sub-contractor.'
                },
                {
                  num: '2',
                  title: 'Transparent Communication',
                  body: 'From your first call to the final installation, you will always know exactly where you stand.'
                },
                {
                  num: '3',
                  title: 'UK-Manufactured uPVC',
                  body: 'Our durable UK-manufactured uPVC faux wood shutters are crafted to the same exacting standard — measured, supplied and fitted in under 5 weeks, and backed by a 10-year warranty.'
                }].
                map((item) =>
                <div key={item.num} className="flex gap-5">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0F1C2E] flex items-center justify-center text-[#FAF9F6] text-sm font-semibold font-display">
                      {item.num}
                    </div>
                    <div>
                      <h3 className="font-display text-[#0F1C2E] font-semibold text-lg mb-1">
                        {item.title}
                      </h3>
                      <p className="text-[#7B8A94] text-base leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section
        id="how-it-works"
        className="py-32 bg-[#0F1C2E] overflow-hidden"
        aria-labelledby="process-heading">
        
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Header */}
          <div
            ref={stepsRef}
            className="text-center mb-20 reveal-hidden">
            
            <p className="text-[#E8DED1]/60 text-sm font-medium tracking-[0.25em] uppercase mb-4">
              The Process
            </p>
            <h2
              id="process-heading"
              className="font-display text-white text-4xl lg:text-5xl font-medium mb-5">
              
              uPVC Shutters: From Enquiry to Fitted in Under 5 Weeks
            </h2>
            <p className="text-[#E8DED1]/70 text-lg max-w-xl mx-auto leading-relaxed">
              One of the fastest turnarounds in the industry — without ever
              compromising on quality.
            </p>
          </div>

          {/* Steps grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {steps.map((step, i) =>
            <StepCard key={step.num} step={step} index={i} />
            )}
          </div>

          {/* CTA banner */}
          <div ref={bannerRef} className="reveal-hidden-scale">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
              <div>
                <p className="font-display text-white text-2xl font-medium mb-1">
                  🏆 uPVC Shutters — Under 5 Weeks, Start to Finish
                </p>
                <p className="text-[#E8DED1]/60 text-base">
                  Most national shutter companies quote 8–12 weeks. We&apos;re significantly
                  faster — and John fits every single one himself.
                </p>
              </div>
              <a
                href="#contact"
                className="flex-shrink-0 bg-white text-[#0F1C2E] font-medium px-7 py-3.5 rounded-full text-sm hover:bg-[#E8DED1] transition-colors">
                
                Book My Survey
              </a>
            </div>
          </div>
        </div>
      </section>
    </>);

}

function StepCard({ step, index }: {step: typeof steps[0];index: number;}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="relative reveal-hidden"
      style={{ transitionDelay: `${index * 120}ms` }}>
      
      {/* Connector line */}
      {index < 3 &&
      <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-white/10 -translate-y-px z-0" />
      }
      <div className="relative z-10">
        <div className="font-display text-5xl font-semibold text-white/10 mb-4 leading-none">
          {step.num}
        </div>
        <h3 className="font-display text-white text-xl font-medium mb-3">{step.title}</h3>
        <p className="text-[#E8DED1]/60 text-sm leading-relaxed">{step.body}</p>
      </div>
    </div>);

}